/*
 * @Description: 实时定位驾驶舱 — B1「团队轨迹」数据 hook
 *   一次拉取窗口内（≤24h）全员轨迹，前端本地做时间轴窗口/快照计算（接口文档 ②）。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { fetchTeamTrack } from '../../../actions/locationCockpit'
import { REALTIME_POLL_INTERVAL_MS, isCockpitMockEnabled } from '../constants'
import { MOCK_TEAM_TRACK } from '../mock/track.mock'
import {
  CockpitEnvelope,
  TeamTrackData,
  TeamTrackMember
} from '../types/cockpit.types'

export interface UseTeamTrackResult {
  trackMembers: TeamTrackMember[]
  loading: boolean
  /** 手动刷新一次 */
  refresh: () => void
}

/**
 * 按 locateTime 升序排序各成员轨迹点。
 * B1 后端返回的 points 不保证时间有序（实测尾部混入更早的点），
 * 下游分段/快照/末点定位都依赖升序，故在数据层统一排好。
 */
function sortTrackMembers(members: TeamTrackMember[]): TeamTrackMember[] {
  return members.map((m) => ({
    ...m,
    points: [...m.points].sort((a, b) => a.locateTime - b.locateTime)
  }))
}

/**
 * 拉取实时定位驾驶舱 B1「团队轨迹」。
 * 按 [startSec, endSec] 一次拉取（≤24h），返回全员轨迹点；
 * 时间轴的窗口裁剪与拖拽快照由调用方在本地计算。
 *
 * @param groupId - 当前团队长 id
 * @param startSec - 起始时间，unix 秒
 * @param endSec - 结束时间，unix 秒
 * @param isToday - 是否当天（当天按 60s 轮询刷新轨迹，历史态只取一次）
 */
export function useTeamTrack(
  groupId: string,
  startSec: number,
  endSec: number,
  isToday: boolean,
  pollIntervalMs: number = REALTIME_POLL_INTERVAL_MS
): UseTeamTrackResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [trackMembers, setTrackMembers] = useState<TeamTrackMember[]>([])
  const [loading, setLoading] = useState(false)
  const aliveRef = useRef(true)

  const load = useCallback(() => {
    if (isMock) {
      setTrackMembers(sortTrackMembers(MOCK_TEAM_TRACK.members))
      return
    }
    if (!groupId || !startSec || !endSec) return

    setLoading(true)
    dispatch(fetchTeamTrack({ groupId, startTime: startSec, endTime: endSec }))
      .then((res: CockpitEnvelope<TeamTrackData>) => {
        if (!aliveRef.current) return
        setLoading(false)
        if (res && res.code === 200) {
          setTrackMembers(sortTrackMembers(res.data?.members ?? []))
        }
      })
      .catch(() => {
        if (!aliveRef.current) return
        setLoading(false)
      })
  }, [dispatch, groupId, startSec, endSec, isMock])

  useEffect(() => {
    aliveRef.current = true
    load()

    // mock 与历史态不轮询；当天按 60s 轮询刷新轨迹（拿新上报点）
    if (isMock || !isToday) {
      return () => {
        aliveRef.current = false
      }
    }
    const timer = window.setInterval(load, pollIntervalMs)
    return () => {
      aliveRef.current = false
      window.clearInterval(timer)
    }
  }, [load, isMock, isToday, pollIntervalMs])

  return { trackMembers, loading, refresh: load }
}
